import { useMemo } from 'react';

import {
  Autocomplete,
  Icons,
  LabelChip,
  LabelChipV2,
  ListItem,
  ShadowDivider,
  AutocompleteProps,
  SxProps,
  Typography,
  Box,
  Stack,
  tokens,
  Tooltip,
} from '@cast/design-system';
import { WorkloadOptimizationState, WorkloadScalingPolicy } from '@cast/types';

import { PreparedWorkload } from 'features/workload-optimization';

import { PolicyIndicator } from './PolicyIndicator';
import { useShowHpaV2 } from '../_hooks/permissions/useShowHpaV2';
import { isEnabledAutoAssignmentPolicy } from '../_utils';

type IndicatorProps = { managementOption: WorkloadOptimizationState };

const Indicator = ({ managementOption }: IndicatorProps) => {
  return (
    <Stack direction="row" gap={4} alignItems="center">
      <PolicyIndicator managementOption={managementOption} />
      <Typography variant="P10R" color={tokens.theme.fg.structural.secondary}>
        {managementOption === WorkloadOptimizationState.MANAGED ? 'ON' : 'OFF'}
      </Typography>
    </Stack>
  );
};

type ScalingPolicyAutocompleteProps = {
  workload: PreparedWorkload;
  policies: WorkloadScalingPolicy[] | undefined;
  disabled: boolean;
  onChange: (newSelectedPolicy: WorkloadScalingPolicy) => void;
  testId?: string;

  // Styling and layout props
  sx?: SxProps;
  popoverProps?: AutocompleteProps<
    WorkloadScalingPolicy,
    WorkloadScalingPolicy
  >['popoverProps'];
  isNamespaceWritableForWorkload: boolean;

  showSystemPolicyLabel?: boolean;

  // Auto-assignment props
  onHeaderClick?: () => void;

  showOptimizationTypeChips?: boolean;
  size?: 'sm' | 'md' | 'lg';
  showEndAdornment?: boolean;
};

type AutoAssignmentOption = { id: 'auto-assignment'; name: 'auto-assignment' };

const isAutoAssignmentOption = (
  option: any
): option is AutoAssignmentOption => {
  return (
    typeof option === 'object' &&
    option !== null &&
    option.id === 'auto-assignment' &&
    option.name === 'auto-assignment'
  );
};
const isManaged = (
  managementOption: WorkloadOptimizationState | undefined
): boolean => managementOption === WorkloadOptimizationState.MANAGED;

export const ScalingPolicyAutocomplete = ({
  workload,
  policies,
  disabled,
  onChange,
  testId = 'scaling-policy-select',
  sx,
  popoverProps,
  showSystemPolicyLabel = false,
  isNamespaceWritableForWorkload,
  onHeaderClick,
  showOptimizationTypeChips = false,
  size = 'sm',
  showEndAdornment = true,
}: ScalingPolicyAutocompleteProps) => {
  const { showHpaV2 } = useShowHpaV2();
  const isAutoEnabled = isEnabledAutoAssignmentPolicy({
    workload,
  });

  const optionsWithAutoAssignment = useMemo(
    () =>
      policies && [
        { id: 'auto-assignment', name: 'auto-assignment' } as const,
        ...[...policies].sort((a, b) => a.name.localeCompare(b.name)),
      ],
    [policies]
  );

  return (
    <Autocomplete<
      WorkloadScalingPolicy | { id: 'auto-assignment'; name: 'auto-assignment' }
    >
      disabled={disabled}
      value={workload.verticalScalingPolicy}
      size={size}
      sx={sx}
      options={optionsWithAutoAssignment}
      optionKey="id"
      optionLabel="name"
      optionValue="id"
      optionDisabled={(option) =>
        isAutoAssignmentOption(option) ? false : option.isCastware
      }
      applySortingWhileSearching={false}
      searchInputProps={{ placeholder: 'Search scaling policies' }}
      inputProps={{
        sx: {
          '& .MuiFilledInput-root.MuiInputBase-formControl': {
            border: tokens.theme.border.structural.neutralSubtle,
          },
          '& .DS-Dropdown-endAdornmentWrapper > :nth-child(2)': {
            display: isAutoEnabled ? 'none' : undefined,
          },
        },
      }}
      endAdornment={
        showEndAdornment ? (
          isAutoEnabled && isNamespaceWritableForWorkload ? (
            <Stack color={disabled ? 'grey.400' : 'blue.500'}>
              <Icons.CastHorizontalLinkDash weight="bold" size={16} />
            </Stack>
          ) : !isNamespaceWritableForWorkload ? (
            <Tooltip title="Restricted access to namespace">
              <Icons.Lock size={16} />
            </Tooltip>
          ) : undefined
        ) : undefined
      }
      popoverProps={popoverProps}
      renderOption={(policy, { onClick, ...props }) => {
        if (isAutoAssignmentOption(policy)) {
          return (
            <ListItem
              {...props}
              onClick={isAutoEnabled ? undefined : onClick}
              disabled={isEnabledAutoAssignmentPolicy({
                workload,
              })}
              sx={{
                pt: 4,
                pb: 10,
                px: 12,
                cursor:
                  onHeaderClick && !isAutoEnabled
                    ? 'pointer'
                    : 'initial!important',
                transition: 'background-color 0.2s',
                backgroundColor: isAutoEnabled
                  ? tokens.theme.bg.status.infoSubtle
                  : 'transparent',
                ...(isAutoEnabled && {
                  color: tokens.theme.fg.status.onInfoSubtle,
                }),
              }}
              data-testid={`${testId}-header`}
            >
              <Stack display="grid" gridTemplateColumns="16px auto" gap={4}>
                {isAutoEnabled ? (
                  <Icons.Check
                    size={16}
                    color={tokens.theme.fg.status.onInfoSubtle}
                  />
                ) : (
                  <Icons.ArrowsClockwise size={16} />
                )}
                <Stack gap={2} maxWidth={460}>
                  <Typography variant="P12M">
                    Enable automatic assignment
                  </Typography>
                  <Typography
                    variant="P10R"
                    color={
                      isAutoEnabled
                        ? tokens.theme.fg.status.onInfoSubtle
                        : tokens.theme.fg.structural.secondary
                    }
                  >
                    The workload will be managed automatically and may move
                    between policies as rules change.
                  </Typography>
                  {workload.suggestedScalingPolicyName && (
                    <Stack direction="row" alignItems="center" gap={4}>
                      <LabelChip
                        size="sm"
                        sx={{
                          backgroundColor: isAutoEnabled
                            ? tokens.theme.bg.status.infoSubtle
                            : tokens.theme.bg.structural.surfaceRaised,
                          color: isAutoEnabled
                            ? tokens.theme.fg.status.onInfoSubtle
                            : tokens.theme.fg.structural.primary,
                        }}
                      >
                        Current match: {workload.suggestedScalingPolicyName}
                      </LabelChip>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </ListItem>
          );
        }
        const { isCastware } = policy;
        const isSystemPolicy = policy.isReadonly && !policy.isCastware;
        return (
          <ListItem
            {...props}
            startAdornment={
              !showHpaV2 && (
                <PolicyIndicator
                  managementOption={
                    policy.recommendationPolicies.managementOption
                  }
                  sx={{ mr: '4px' }}
                />
              )
            }
            onClick={isCastware ? undefined : onClick}
            sx={
              isCastware
                ? {
                    cursor: 'initial !important',
                    '&:hover': { backgroundColor: 'initial' },
                  }
                : {
                    // if auto enabled the auto assigned policy on the list become highlighted, the below code fix it.
                    ...(isAutoEnabled
                      ? {
                          '&.DS-Option-selected': {
                            color: 'initial!important',
                            backgroundColor: 'initial!important',
                          },
                        }
                      : {}),
                  }
            }
          >
            {showSystemPolicyLabel ? (
              <Stack
                direction="row"
                gap={4}
                justifyContent="space-between"
                sx={{ width: '100%' }}
              >
                <Typography variant="P12R">{policy.name}</Typography>
                {isSystemPolicy && (
                  <Typography
                    variant="P12R"
                    color={tokens.theme.fg.structural.secondary}
                  >
                    system policy
                  </Typography>
                )}
              </Stack>
            ) : (
              <Typography variant="P12R">{policy.name}</Typography>
            )}
          </ListItem>
        );
      }}
      startAdornment={(selectedPolicy) => {
        if (isAutoAssignmentOption(selectedPolicy) || !selectedPolicy) {
          return undefined;
        }

        const showPolicyIndicator = !showHpaV2;
        if (!showPolicyIndicator && !showOptimizationTypeChips) {
          return undefined;
        }

        return (
          <Stack direction="row" alignItems="center" gap={4}>
            {showPolicyIndicator && (
              <PolicyIndicator
                managementOption={
                  selectedPolicy.recommendationPolicies.managementOption
                }
                disabled={disabled}
              />
            )}
            {showOptimizationTypeChips && (
              <>
                <LabelChipV2
                  label="V"
                  size="xs"
                  color={
                    isManaged(
                      selectedPolicy.recommendationPolicies.managementOption
                    )
                      ? 'blue'
                      : 'grey'
                  }
                />
                <LabelChipV2
                  label="H"
                  size="xs"
                  color={
                    isManaged(selectedPolicy.hpaSettings?.managementOption)
                      ? 'blue'
                      : 'grey'
                  }
                />
              </>
            )}
          </Stack>
        );
      }}
      onChange={(newSelectedPolicy) => {
        if (isAutoAssignmentOption(newSelectedPolicy)) {
          onHeaderClick?.();
          return;
        }
        if (newSelectedPolicy.id !== workload.verticalScalingPolicy?.id) {
          onChange(newSelectedPolicy);
        }
      }}
      Footer={
        !showHpaV2 && (
          <Box position="relative">
            <ShadowDivider
              opacity={0.2}
              sx={{ position: 'absolute', top: 0 }}
            />
            <Stack direction="row" gap={12} py={8} px={12}>
              <Typography
                variant="P10R"
                color={tokens.theme.fg.structural.secondary}
              >
                Automatic optimization:
              </Typography>
              <Indicator managementOption={WorkloadOptimizationState.MANAGED} />
              <Indicator
                managementOption={WorkloadOptimizationState.READ_ONLY}
              />
            </Stack>
          </Box>
        )
      }
      testId={testId}
    />
  );
};
